import { z } from 'zod';
import { cursorPagination, isoDate, satang, thaiPhone } from './schemas.js';

/* ------------------------------------------------------------- ผู้บริจาค */

export const DONOR_TYPES = ['individual', 'corporate', 'anonymous', 'group'] as const;
export const donorTypeEnum = z.enum(DONOR_TYPES);

export const DONOR_TYPE_LABEL_TH: Record<(typeof DONOR_TYPES)[number], string> = {
  individual: 'บุคคลธรรมดา',
  corporate: 'นิติบุคคล',
  anonymous: 'ไม่ประสงค์ออกนาม',
  group: 'คณะบุคคล',
};

/**
 * เลขประจำตัวผู้เสียภาษี — บุคคลธรรมดาใช้เลขบัตรประชาชน 13 หลัก
 * นิติบุคคลใช้เลขทะเบียน 13 หลักเช่นกัน จึงตรวจแค่รูปแบบ ไม่ตรวจ checksum
 * (เลขนิติบุคคลใช้สูตร checksum คนละแบบกับบัตรประชาชน)
 */
export const taxId = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, ''))
  .refine((v) => /^\d{13}$/.test(v), 'เลขประจำตัวผู้เสียภาษีต้องเป็นตัวเลข 13 หลัก');

export const createDonorSchema = z
  .object({
    donorType: donorTypeEnum.default('individual'),
    title: z.string().trim().max(32).optional(),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
    orgName: z.string().trim().max(200).optional(),
    displayName: z.string().trim().min(1).max(200).optional(),

    taxId: taxId.optional(),
    phone: thaiPhone.optional(),
    email: z.string().trim().email().max(255).optional(),
    addressLine: z.string().trim().max(500).optional(),
    areaId: z.string().max(40).optional(),
    postalCode: z.string().regex(/^\d{5}$/).optional(),

    wantsReceipt: z.boolean().default(false),
    receiptName: z.string().trim().max(200).optional(),
    receiptAddress: z.string().trim().max(500).optional(),
    isAnonymous: z.boolean().default(false),

    marketingOptIn: z.boolean().default(false),
    note: z.string().trim().max(2000).optional(),
  })
  .refine(
    (d) => d.displayName || d.orgName || d.firstName || d.isAnonymous,
    'ต้องระบุชื่อผู้บริจาคอย่างน้อยหนึ่งอย่าง',
  );

export const updateDonorSchema = z.object({
  donorType: donorTypeEnum.optional(),
  title: z.string().trim().max(32).optional(),
  firstName: z.string().trim().max(100).optional(),
  lastName: z.string().trim().max(100).optional(),
  orgName: z.string().trim().max(200).optional(),
  displayName: z.string().trim().min(1).max(200).optional(),
  taxId: taxId.optional(),
  phone: thaiPhone.optional(),
  email: z.string().trim().email().max(255).optional(),
  addressLine: z.string().trim().max(500).optional(),
  postalCode: z.string().regex(/^\d{5}$/).optional(),
  wantsReceipt: z.boolean().optional(),
  receiptName: z.string().trim().max(200).optional(),
  receiptAddress: z.string().trim().max(500).optional(),
  isAnonymous: z.boolean().optional(),
  marketingOptIn: z.boolean().optional(),
  note: z.string().trim().max(2000).optional(),
  status: z.enum(['active', 'inactive', 'blocked']).optional(),
});

export const listDonorsQuerySchema = cursorPagination.extend({
  q: z.string().trim().max(100).optional(),
  donorType: donorTypeEnum.optional(),
  status: z.enum(['active', 'inactive', 'blocked']).optional(),
  sort: z.enum(['recent', 'total_amount', 'name']).default('recent'),
});

/* --------------------------------------------------------------- แคมเปญ */

export const CAMPAIGN_STATUSES = ['draft', 'active', 'paused', 'closed'] as const;
export const campaignStatusEnum = z.enum(CAMPAIGN_STATUSES);

export const CAMPAIGN_STATUS_LABEL_TH: Record<(typeof CAMPAIGN_STATUSES)[number], string> = {
  draft: 'ฉบับร่าง',
  active: 'เปิดรับบริจาค',
  paused: 'พักชั่วคราว',
  closed: 'ปิดแล้ว',
};

export const createCampaignSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, 'code ต้องเป็นตัวพิมพ์ใหญ่ ตัวเลข และ _'),
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4000).optional(),
  goalAmount: satang.optional(),
  startDate: isoDate.optional(),
  endDate: isoDate.optional(),
  coverKey: z.string().max(255).optional(),
});

export const updateCampaignSchema = createCampaignSchema
  .omit({ code: true })
  .partial()
  .extend({ status: campaignStatusEnum.optional() });

/* ---------------------------------------------------------- เงินบริจาค */

export const DONATION_CHANNELS = [
  'bank_transfer',
  'promptpay',
  'cash',
  'cheque',
  'in_kind',
  'other',
] as const;

export const DONATION_STATUSES = [
  'pending_verify',
  'verified',
  'rejected',
  'duplicate',
  'refunded',
  'void',
] as const;

export const donationChannelEnum = z.enum(DONATION_CHANNELS);
export const donationStatusEnum = z.enum(DONATION_STATUSES);

export const DONATION_CHANNEL_LABEL_TH: Record<(typeof DONATION_CHANNELS)[number], string> = {
  bank_transfer: 'โอนผ่านธนาคาร',
  promptpay: 'พร้อมเพย์',
  cash: 'เงินสด',
  cheque: 'เช็ค',
  in_kind: 'สิ่งของ',
  other: 'อื่น ๆ',
};

export const DONATION_STATUS_LABEL_TH: Record<(typeof DONATION_STATUSES)[number], string> = {
  pending_verify: 'รอตรวจสอบ',
  verified: 'ยืนยันแล้ว',
  rejected: 'ปฏิเสธ',
  duplicate: 'รายการซ้ำ',
  refunded: 'คืนเงินแล้ว',
  void: 'ยกเลิก',
};

/** ผู้บริจาคแจ้งโอนเองผ่าน LIFF */
export const reportDonationSchema = z.object({
  amount: satang.refine((v) => v > 0, 'จำนวนเงินต้องมากกว่า 0'),
  donatedAt: z.string().datetime({ offset: true }),
  channel: donationChannelEnum.default('promptpay'),
  bankAccountId: z.string().max(40).optional(),
  campaignId: z.string().max(40).optional(),
  slipKey: z.string().min(1).max(255),
  slipSha256: z.string().length(64),
  payerName: z.string().trim().max(200).optional(),
  slipRef: z.string().trim().max(64).optional(),
  earmarkNote: z.string().trim().max(500).optional(),
  wantsReceipt: z.boolean().default(false),
  receipt: z
    .object({
      name: z.string().trim().min(1).max(200),
      taxId: taxId.optional(),
      address: z.string().trim().max(500).optional(),
    })
    .optional(),
});

/** เจ้าหน้าที่การเงินบันทึกด้วยมือจากเว็บ */
export const createDonationSchema = z.object({
  donorId: z.string().max(40).optional(),
  campaignId: z.string().max(40).optional(),
  amount: satang.refine((v) => v > 0, 'จำนวนเงินต้องมากกว่า 0'),
  donatedAt: z.string().datetime({ offset: true }),
  channel: donationChannelEnum,
  bankAccountId: z.string().max(40).optional(),
  slipKey: z.string().max(255).optional(),
  slipSha256: z.string().length(64).optional(),
  slipRef: z.string().trim().max(64).optional(),
  payerName: z.string().trim().max(200).optional(),
  purpose: z.enum(['general', 'campaign', 'earmarked']).default('general'),
  earmarkNote: z.string().trim().max(500).optional(),
  note: z.string().trim().max(2000).optional(),
  /** บริจาคสิ่งของ */
  items: z
    .array(
      z.object({
        itemName: z.string().trim().min(1).max(200),
        quantity: z.number().positive().max(1_000_000),
        unit: z.string().trim().max(32).optional(),
        estimatedValue: satang.optional(),
      }),
    )
    .max(50)
    .default([]),
});

export const verifyDonationSchema = z.object({
  /** แก้ยอดให้ตรงสลิปได้ — ค่าที่ผู้บริจาคกรอกถือเป็นแค่คำแนะนำ */
  amount: satang.refine((v) => v > 0).optional(),
  donatedAt: z.string().datetime({ offset: true }).optional(),
  slipRef: z.string().trim().max(64).optional(),
  bankAccountId: z.string().max(40).optional(),
  campaignId: z.string().max(40).optional(),
  donorId: z.string().max(40).optional(),
  /** สร้างผู้บริจาคใหม่พร้อมกับการยืนยัน */
  createDonor: createDonorSchema.optional(),
  issueReceipt: z.boolean().default(false),
  note: z.string().trim().max(2000).optional(),
});

export const rejectDonationSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  markDuplicate: z.boolean().default(false),
});

export const listDonationsQuerySchema = cursorPagination.extend({
  status: donationStatusEnum.optional(),
  channel: donationChannelEnum.optional(),
  campaignId: z.string().max(40).optional(),
  donorId: z.string().max(40).optional(),
  from: z.string().max(40).optional(),
  to: z.string().max(40).optional(),
  q: z.string().trim().max(100).optional(),
});

/* ----------------------------------------------------------- ใบเสร็จ */

export const issueReceiptSchema = z.object({
  receiptName: z.string().trim().min(1).max(200).optional(),
  receiptAddress: z.string().trim().max(500).optional(),
  taxId: taxId.optional(),
});

export const voidReceiptSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

/* -------------------------------------------------------- PromptPay QR */

export const promptPayQuerySchema = z.object({
  bankAccountId: z.string().max(40).optional(),
  // มาจาก query string จึงต้อง coerce — ไม่งั้น ?amount=50000 จะไม่ผ่าน validation
  amount: z.coerce.number().int().min(0).max(99_999_999_999).optional(),
});

export type CreateDonorInput = z.infer<typeof createDonorSchema>;
export type ReportDonationInput = z.infer<typeof reportDonationSchema>;
export type CreateDonationInput = z.infer<typeof createDonationSchema>;
export type VerifyDonationInput = z.infer<typeof verifyDonationSchema>;
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

/* ------------------------------------------------- ตั้งค่าบัญชีรับบริจาค */

/**
 * บัญชีรับบริจาค — เป็นค่าที่ **เปลี่ยนแล้วเงินเปลี่ยนปลายทางทันที**
 * จึงตรวจรูปแบบเข้มและบันทึก audit ทุกครั้งที่แก้
 */
export const bankAccountSchema = z.object({
  bankCode: z.string().trim().regex(/^\d{3}$/, 'รหัสธนาคารต้องเป็นตัวเลข 3 หลัก'),
  bankName: z.string().trim().min(1).max(100),
  accountNo: z
    .string()
    .trim()
    .max(30)
    .refine((v) => v.replace(/\D/g, '').length >= 10, 'เลขบัญชีต้องมีอย่างน้อย 10 หลัก'),
  accountName: z.string().trim().min(1).max(200),
  /** เบอร์โทร 10 หลัก · เลขประจำตัว 13 หลัก · e-wallet 15 หลัก — ว่างได้ถ้าไม่ใช้ QR */
  promptPayId: z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .refine(
      (v) => v === '' || [10, 13, 15].includes(v.replace(/\D/g, '').length),
      'รหัสพร้อมเพย์ต้องเป็นเบอร์โทร 10 หลัก เลขประจำตัว 13 หลัก หรือ e-wallet 15 หลัก',
    )
    .optional(),
  isActive: z.boolean().optional(),
});

export const updateBankAccountSchema = bankAccountSchema.partial();

/** ข้อมูลมูลนิธิที่ขึ้นบนหัวใบเสร็จ */
export const orgProfileSchema = z.object({
  nameTh: z.string().trim().min(1).max(200),
  taxId: z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .refine((v) => v === '' || /^\d{13}$/.test(v), 'เลขประจำตัวผู้เสียภาษีต้องเป็นตัวเลข 13 หลัก')
    .optional(),
  address: z.string().trim().max(500).optional(),
  phone: z.string().trim().max(50).optional(),
  receiptFooter: z.string().trim().max(200).optional(),
  // ลำดับที่ในประกาศกระทรวงการคลัง — ไม่มีเลขนี้ ผู้บริจาคลดหย่อนภาษีไม่ได้
  charityAnnouncementNo: z.string().trim().max(50).optional(),
});
